import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Spinner } from '@phosphor-icons/react';
import axios from 'axios';
import { useSubscription } from '../context/SubscriptionContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL = 2000;
const MAX_POLL_ATTEMPTS = 20;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling');
  const [tx, setTx] = useState(null);
  const attemptsRef = useRef(0);
  const { refresh: refreshSub } = useSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        setTx(data);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          await refreshSub();
          return;
        }
        if (data.payment_status === 'expired' || data.payment_status === 'failed') {
          setStatus('failed');
          return;
        }
      } catch {
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setStatus('timeout');
          return;
        }
      }
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        setStatus('timeout');
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshSub]);

  return (
    <div className="min-h-screen flex items-center justify-center noise-bg" style={{ background: '#0A0B0E' }} data-testid="payment-success-page">
      <div className="w-full max-w-md p-8 bg-[#12141A] border border-white/10 text-center">
        {status === 'polling' && (
          <>
            <Spinner size={56} weight="duotone" className="text-[#A855F7] mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-heading font-bold text-white mb-2">Confirming payment...</h2>
            <p className="text-sm text-[#94A3B8]">This usually takes a few seconds.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle size={64} weight="duotone" className="text-[#00FF66] mx-auto mb-4" />
            <h2 className="text-3xl font-heading font-black tracking-tighter text-white mb-2">Payment successful!</h2>
            <p className="text-sm text-[#94A3B8] mb-6">
              Your subscription is now active. Enjoy all the advanced features.
            </p>
            {tx && (
              <div className="text-xs font-mono text-[#64748B] mb-6">
                Amount: ₹{tx.amount?.toLocaleString('en-IN')} {tx.currency?.toUpperCase()}
              </div>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-[#2563EB] hover:bg-blue-600 text-white py-3 text-sm font-medium transition-colors"
              data-testid="go-to-dashboard"
            >
              Go to Dashboard
            </button>
          </>
        )}
        {status === 'failed' && (
          <>
            <XCircle size={64} weight="duotone" className="text-[#FF3366] mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-white mb-2">Payment did not complete</h2>
            <p className="text-sm text-[#94A3B8] mb-6">Please try again or use a different payment method.</p>
            <Link to="/pricing" className="w-full bg-[#2563EB] hover:bg-blue-600 text-white py-3 text-sm font-medium transition-colors inline-block">
              Back to Plans
            </Link>
          </>
        )}
        {(status === 'timeout' || status === 'error') && (
          <>
            <Spinner size={56} weight="duotone" className="text-[#FBBF24] mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-white mb-2">Still processing</h2>
            <p className="text-sm text-[#94A3B8] mb-6">Your payment is being confirmed by the bank. Check back in a minute.</p>
            <Link to="/dashboard" className="w-full bg-[#2563EB] hover:bg-blue-600 text-white py-3 text-sm font-medium transition-colors inline-block">
              Go to Dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
